export interface StoredAuthSession {
  token: string;
  refreshToken?: string;
  user?: unknown;
}

export const getStoredAccessToken = (): string | null =>
  localStorage.getItem('token') || sessionStorage.getItem('token');

export const getStoredUser = (): string | null =>
  localStorage.getItem('user') || sessionStorage.getItem('user');

export const getRefreshStorage = (): Storage | null => {
  if (localStorage.getItem('refreshToken')) {
    return localStorage;
  }
  if (sessionStorage.getItem('refreshToken')) {
    return sessionStorage;
  }
  return null;
};

export const clearStoredAuth = (): void => {
  localStorage.removeItem('token');
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  localStorage.removeItem('tokenStoredAt');
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('refreshToken');
  sessionStorage.removeItem('user');
  sessionStorage.removeItem('tokenStoredAt');
};

export const storeAuthSession = (
  session: StoredAuthSession,
  rememberMe: boolean
): void => {
  clearStoredAuth();

  const storage = rememberMe ? localStorage : sessionStorage;
  storage.setItem('token', session.token);

  if (session.refreshToken) {
    storage.setItem('refreshToken', session.refreshToken);
  }

  if (session.user !== undefined) {
    storage.setItem('user', JSON.stringify(session.user));
  }
};

/** Decode a JWT payload without external dependencies. Returns null on failure. */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

/** Check whether a JWT's `exp` claim is in the past. */
function isTokenExpired(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') return true;
  return Date.now() >= payload.exp * 1000;
}

/**
 * Clear stored auth if the session is no longer recoverable.
 *
 * - access expired + refresh valid → keep session (interceptor will refresh)
 * - refresh expired/invalid → clearStoredAuth
 * - no refresh + access expired → clearStoredAuth
 */
export const clearExpiredTokens = (): void => {
  const accessToken = getStoredAccessToken();
  if (!accessToken) return;

  if (!isTokenExpired(accessToken)) return; // access token still valid

  // Access token is expired — check refresh token
  const refreshToken =
    localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');

  if (!refreshToken || isTokenExpired(refreshToken)) {
    // No refresh token or it's also expired → session is unrecoverable
    clearStoredAuth();
  }
  // If refresh token exists and is valid, keep session — interceptor will handle refresh
};
